import { Repository } from '../../../core/context/repository';
import { type CustomAttribute } from './entity';

type Kind = 'asset' | 'item';

interface CustomAttributeResource {
    types: CustomAttribute[];
    attributes: CustomAttribute[];
}

export class CustomAttributes extends Repository<CustomAttribute> {
    getAssetAttributes(): CustomAttribute[] {
        return this.getType(true, false);
    }

    getAssetTypes(): CustomAttribute[] {
        return this.getType(true, true);
    }

    getSelectedAssetTypes(): CustomAttribute[] {
        const types = this.getAssetTypes();
        const selected = this.getSelectedAttrIds('asset');
        return types.filter((t) => this.childrenSome(t.childrenIds, selected));
    }

    getSelectedAssetAttributes(): CustomAttribute[] {
        return this.getAssetAttributes().filter((a) => a.isSelected);
    }

    getSelectedIds(): string[] {
        const kind: Kind = 'asset';
        const { types } = this.getData(kind);
        const selected = this.getSelectedAttrIds(kind);

        const children = Array.from(selected);
        const parents = types
            .filter((t) => this.childrenEvery(t.childrenIds, selected))
            .map((t) => t.id);

        return [...children, ...parents];
    }

    getPartiallySelectedIds(): string[] {
        const kind: Kind = 'asset';
        const { types } = this.getData(kind);
        const selected = this.getSelectedAttrIds(kind);

        return types
            .filter(
                (t) =>
                    this.childrenSome(t.childrenIds, selected) &&
                    !this.childrenEvery(t.childrenIds, selected),
            )
            .map((t) => t.id);
    }

    save(selectedIds: string[]): void {
        this.items.forEach(
            (attr) => (attr.isSelected = selectedIds.includes(attr.id)),
        );
    }

    private getData(_: Kind): CustomAttributeResource {
        return {
            types: this.getAssetTypes(),
            attributes: this.getAssetAttributes(),
        };
    }

    private getSelectedAttrIds(kind: Kind): Set<string> {
        const { attributes } = this.getData(kind);
        return new Set(attributes.filter((a) => a.isSelected).map((a) => a.id));
    }

    private childrenSome(ids: string[] = [], selected: Set<string>): boolean {
        return Boolean(ids.some((id) => selected.has(id)));
    }

    private childrenEvery(ids: string[] = [], selected: Set<string>): boolean {
        return Boolean(
            ids && ids.length && ids.every((id) => selected.has(id)),
        );
    }

    private getType(isAsset: boolean, isArr: boolean): CustomAttribute[] {
        return this.items.filter(
            (attr) =>
                attr.isAsset === isAsset &&
                Array.isArray(attr.childrenIds) === isArr,
        );
    }
}
